import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  MoonIcon,
  SunIcon
} from '@heroicons/react/24/outline';
import { navigationIcons } from './ui/NavigationIcons';
import HeaderNavigation from './HeaderNavigation';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/',
      current: location.pathname === '/',
      icon: navigationIcons.dashboard
    },
    {
      name: 'Call Logs',
      href: '/calls',
      current: location.pathname === '/calls',
      icon: navigationIcons.callLogs
    },
    {
      name: 'Analytics',
      href: '/analytics',
      current: location.pathname === '/analytics',
      icon: navigationIcons.analytics
    },
    {
      name: 'Agents',
      href: '/agents',
      current: location.pathname === '/agents',
      icon: navigationIcons.resources
    },

  ];

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>

      {/* Header - Improved Spacing and Layout */}
      <header
        className={`
          sticky top-0 z-50 shadow-sm border-b transition-all duration-200 h-16
          ${isDarkMode
            ? 'bg-gray-900 border-gray-700 shadow-black/10'
            : 'bg-white border-neutral-200 shadow-neutral-900/5'
          }
        `}
        role="banner"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Company Logo - Left aligned, vertically centered */}
            <div className="flex items-center gap-3">
              <div
                className={`
                  w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl 
                  flex items-center justify-center
                  bg-gradient-to-br from-primary-500 to-primary-600
                  shadow-md sm:shadow-lg transition-all duration-300 hover:scale-105
                  ${isDarkMode ? 'shadow-primary-900/50' : 'shadow-primary-500/25'}
                `}
                role="img"
                aria-label="Voice Assistant company logo"
              >
                <span className="text-white font-bold text-sm sm:text-base tracking-tight" aria-hidden="true">VA</span>
              </div>
              <div className="hidden sm:block">
                <h1 className={`text-base font-bold tracking-tight transition-colors duration-200 ${isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                  Voice Assistant
                </h1>
                <p className={`text-xs font-medium transition-colors duration-200 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Dashboard
                </p>
              </div>
            </div>

            {/* Right side - Navigation Items and User Controls, vertically centered */}
            <div className="flex items-center gap-6">
              <HeaderNavigation isDarkMode={isDarkMode} navigation={navigation} />

              <div className="flex items-center gap-3 sm:gap-4">
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className={`
                    p-2 rounded-xl transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                    ${isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDarkMode}
                >
                  {isDarkMode ? (
                    <SunIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <MoonIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>

                {/* User Email - Hidden on mobile, with max width to prevent overflow */}
                <span
                  className={`hidden sm:block text-sm max-w-[200px] truncate transition-colors duration-200 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  aria-label={`Logged in as ${user?.email}`}
                >
                  {user?.email}
                </span>

                {/* Logout Button */}
                <button
                  onClick={logout}
                  className={`
                    p-2 rounded-xl transition-colors duration-200 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                    text-sm px-3 py-2 border
                    ${isDarkMode
                      ? 'text-gray-300 border-gray-600 hover:text-white hover:bg-gray-800 hover:border-gray-500'
                      : 'text-gray-600 border-gray-300 hover:text-gray-900 hover:bg-gray-100 hover:border-gray-400'
                    }
                  `}
                  aria-label="Sign out of your account"
                >
                  <span className="hidden sm:inline">Logout</span>
                  <span className="sm:hidden">Exit</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main
        id="main-content"
        className={`
          pt-4 sm:pt-6 lg:pt-8 transition-colors duration-200 
          ${isDarkMode ? 'bg-black' : 'bg-gray-50'}
        `}
        role="main"
        aria-label="Main content"
        tabIndex={-1}
      >
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;